import { useState, useContext, useEffect, useRef } from "react";
import { SidebarContext } from "core/contexts/sidebar/sidebar";
import { Drawer, useMediaQuery, useTheme } from "@mui/material";
import { Collapse, ListItemText, Typography } from "@mui/material";

import AccentureWhiteLogo from "assets/images/AccentureWhiteLogo.svg";
import AccentureBlackLogo from "assets/images/AccentureBlackLogo.png";
import AccentureIcon from "assets/AccentureIcon";
import { SidebarType } from "./sidebar.types";
import { IconButton, styled, Box, Backdrop } from "@mui/material";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import MessageOutlined from "@mui/icons-material/MessageOutlined";
import CrossBtnIcon from "assets/CrossBtnIcon";
import {
  CustomDrawer,
  StyledHeader,
  DrawerHeaderSticky,
  CustomDrawerHeader,
  NavLogo,
  SidebarDataLayout,
  SidebarItemsContainer,
  SideBarMenuContainer,
  DrawerTitle,
  CustomIconButton,
  FavoritesContainer,
  FavoritesNumber,
  SidebarChatRow,
  CategoryHeader
} from "./sidebar.styled";
import SidebarItems from "./sidebarItems";
import VectorHamburgerIcon from "assets/VectorHamburgerIcon";
import MinimizeIcon from "assets/MinimizeIcon";
import { AuthContext } from "core/contexts/auth/auth";
import { SidebarSearch } from "./sidebarSearch";
import { useAppSelector, useAppDispatch } from "core/store/reduxStore";
import { useSocket } from "modules/chat/store/socketContext";
import MenuClose from "assets/MenuClose";
import { SpaceInvaderCanvas } from "core/components/global/icons/spaceInvaders/spaceInvaders";
import colors from "core/components/routing/pages/sidebarExpandedArea/colors";
import { useNavigate } from "react-router";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { useAppTheme } from "core/contexts/theme/theme";
import AccentureLogo from "assets/AccentureLogo";
import { ArrowDown } from "assets/ArrowDown";
import { ArrowUp } from "assets/ArrowUp";
import { ConversationType } from "modules/chat/components/chat.type";
import { getFavorites } from "core/components/routing/pages/sidebarExpandedArea/homeService";
import { formatMessageTime } from "core/utils/dateTimeUtils";
import { getConversationsFromApi } from "modules/chat/services/chatService";
import {
  setActiveModel,
  setCurrentConversation,
  setConversations,
} from "modules/chat/store/conversationList";
import HistoryIcon from "assets/HistoryIcon";

// Add a styled backdrop component for mobile
const MenuBackdrop = styled(Backdrop)({
  zIndex: 1,
  color: "#fff",
});

export const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  ...theme.mixins.toolbar,
  minHeight: "57px !important",
}));

const MenuItems = (isDrawerOpen) => {
  const { amsUser } = useContext(AuthContext);
  const { routeData, setShowHideTooltip, setSidesideBarItemPositionRight } =
    useContext(SidebarContext);
  const sidebarMenuRef = useRef(null);
  const [userGroups, setUserGroups] = useState(amsUser?.groups || []);
  const { apptheme } = useAppTheme();

  const handleMouseOut = (event) => {
    const SidebarDataLayout = event.target.getBoundingClientRect();

    if (
      event.clientY > SidebarDataLayout.bottom ||
      event.clientY < SidebarDataLayout.top
    ) {
      // Mouse left from below
      setShowHideTooltip(false);
    }
  };

  useEffect(() => {
    // Initialize the ResizeObserver to watch for width changes
    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        // Access the contentRect to get the width and height
        const { width, height } = entry.contentRect;
        setSidesideBarItemPositionRight(width + 15);
      });
    });

    // Start observing the sidebar container for size changes
    if (sidebarMenuRef.current) {
      resizeObserver.observe(sidebarMenuRef.current);
    }

    // Cleanup the observer when the component is unmounted or the ref changes
    return () => {
      if (sidebarMenuRef.current) {
        resizeObserver.unobserve(sidebarMenuRef.current);
      }
    };
  }, [setShowHideTooltip]);

  const checkRouteAccess = (route: SidebarType, groups: string[]) => {
    const routeGroups = route.sidebarProps?.adGroups || [];

    // Check if there are no groups required for the route or if user has matching groups
    if (
      routeGroups.length === 0 ||
      routeGroups.some((group) => groups.includes(group))
    ) {
      return true;
    } else {
      return false;
    }
  };

  useEffect(() => {
    if (amsUser?.groups) {
      setUserGroups(amsUser.groups);
    }
  }, [amsUser]);

  return (
    <SideBarMenuContainer ref={sidebarMenuRef} onMouseLeave={handleMouseOut}>
      {routeData &&
        routeData.map((route: SidebarType, index) =>
          checkRouteAccess(route, amsUser?.groups || []) === true ? (
            <SidebarItems
              item={route}
              key={index}
              userGroups={userGroups}
              isDrawerOpen={isDrawerOpen}
            />
          ) : (
            <></>
          )
        )}
    </SideBarMenuContainer>
  );
};

interface HeaderIconClickProps {
  openDrawer: boolean;
  screenSize?: string;
}

export default function SidebarMenu() {
  const { isDrawerOpen, setIsDrawerOpen, setActiveItem, activeItem, setSidebarActiveState } =
    useContext(SidebarContext);
  const { amsUser, checkAMSToken } = useContext(AuthContext);
  const [active, setActive] = useState(activeItem === "favorite");
  const [conversationsList, setConversationsList] = useState<any>([]);
  const [favoritesCount, setFavoritesCount] = useState(0);
  const [historySections, setHistorySections] = useState({
    today: true,
    yesterday: false,
    pastWeek: false, 
    lastMonth: false,
    older: false
  });
  const theme = useTheme();
  const sideState = useAppSelector((state) => state.sidebar);
  const { apptheme, toggleTheme } = useAppTheme();
  const dispatch = useAppDispatch();

  // Use useMediaQuery to detect if screen width is larger than the laptop size
  const isSmallerThan = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (isSmallerThan) {
      // Ensure drawer is closed initially on small screens
      setIsDrawerOpen(false);
    } else {
      setIsDrawerOpen(false);
    }
  }, [isSmallerThan, setIsDrawerOpen]);

  const handleHeaderIconClick = ({ openDrawer }: HeaderIconClickProps) => {
    setIsDrawerOpen(openDrawer);
  };

  const convoState = useAppSelector((state) => state.chatConversations);

  const getRelativeTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();

    const isSameDay = date.toDateString() === now.toDateString();

    const yesterday = new Date();
    yesterday.setDate(now.getDate() - 1);
    const isYesterday = date.toDateString() === yesterday.toDateString();

    const daysDiff = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (isSameDay) return "today";
    if (isYesterday) return "yesterday";
    if (daysDiff <= 14) return "pastWeek";
    if (daysDiff <= 34) return "lastMonth";

    return "older";
  };
  
  // Group conversations by date category
  interface ConversationGroup {
    key: string;
    conversation: ConversationType;
  }
  
  interface GroupedConversations {
    today: ConversationGroup[];
    yesterday: ConversationGroup[];
    pastWeek: ConversationGroup[];
    lastMonth: ConversationGroup[];
    older: ConversationGroup[];
  }
  
  const groupConversationsByDate = (conversations: Record<string, any>): GroupedConversations => {
    const groups: GroupedConversations = {
      today: [],
      yesterday: [],
      pastWeek: [],
      lastMonth: [],
      older: []
    };
    
    Object.entries(conversations).forEach(([key, conversation]: [string, any]) => {
      const dateCategory = getRelativeTime(conversation.updatedAt || conversation.createdAt || "");
      groups[dateCategory as keyof GroupedConversations].push({key, conversation});
    });
    
    // Sort each group by date (newest first)
    Object.keys(groups).forEach(group => {
      groups[group as keyof GroupedConversations].sort((a, b) => {
        const dateA = new Date(a.conversation.updatedAt || a.conversation.createdAt || 0).getTime();
        const dateB = new Date(b.conversation.updatedAt || b.conversation.createdAt || 0).getTime();
        return dateB - dateA;
      });
    });
    
    return groups;
  };

  const navigate = useNavigate();
  const handleSelectConversation = (chat: ConversationType) => {
    dispatch(setCurrentConversation(chat.conversationId));
    navigate("/console/chat"); // Redirect to the chat component
    setIsDrawerOpen(false); // Close the sidebar when a conversation is selected
  };

  async function fetchFavorites() {
    const token = await checkAMSToken();
    const data = await getFavorites(token);
    if (data !== null) {
      setFavoritesCount(data.length);
    }
  }

  useEffect(() => {
    setFavoritesCount(sideState.favoritesCount);
  }, [sideState.favoritesCount]);
  
  // Update active state whenever activeItem changes
  useEffect(() => {
    setActive(activeItem === "favorite");
  }, [activeItem]);

  useEffect(() => {
    fetchFavorites();
    
    // Load conversations on startup after token fetch
    const loadConversationsOnStartup = async () => {
      if (amsUser && checkAMSToken) {
        try {
          const token = await checkAMSToken();
          if (token) {
            const response = await getConversationsFromApi(token);
            if (response && response.length > 0) {
              const conversationMap = response.reduce((acc, conversation) => {
                acc[conversation.conversationId] = conversation;
                return acc;
              }, {});
              dispatch(setConversations({ conversations: conversationMap }));
              // Set initial section visibility (Today expanded)
              setHistorySections({
                today: true,
                yesterday: false,
                pastWeek: false,
                lastMonth: false,
                older: false
              });
            }
          }
        } catch (error) {
          console.error("Error loading conversations on startup:", error);
        }
      }
    };
    
    loadConversationsOnStartup();
  }, [amsUser]);

  useEffect(() => {
    if (convoState) {
      setConversationsList(convoState.history);
    }
  }, [convoState.history]);

  const drawerHeader = (): JSX.Element => {
    return (
      <DrawerHeaderSticky isDrawerOpen={isDrawerOpen}>
        <CustomDrawerHeader className="mt-1" isDrawerOpen={isDrawerOpen}>
          {isDrawerOpen ? (
            <DrawerTitle className="ms-3">mySecurity AI Engine</DrawerTitle>
          ) : (
            <CustomIconButton
              disableRipple
              onClick={() =>
                handleHeaderIconClick({
                  openDrawer: true,
                })
              }
            >
              {apptheme.name === "dark-theme" ? (
                <AccentureIcon />
              ) : (
                <AccentureIcon />
              )}
            </CustomIconButton>
          )}
          {isDrawerOpen && (
            <IconButton
              onClick={() =>
                handleHeaderIconClick({
                  openDrawer: false,
                })
              }
              data-testid="hamburger-btn"
            >
              {window.innerWidth <= 900 ? <CrossBtnIcon /> : <MenuClose />}
            </IconButton>
          )}
        </CustomDrawerHeader>
      </DrawerHeaderSticky>
    );
  };

  // Handle backdrop click to close drawer on mobile
  const handleBackdropClick = () => {
    setIsDrawerOpen(false);
  };

  return (
    <SidebarDataLayout>
      {/* For larger screens */}
      {!isSmallerThan && (
        <>
          <CustomDrawer
            variant="permanent"
            anchor="left"
            open={isDrawerOpen.toString().toLowerCase()}
          >
            <SidebarItemsContainer isDrawerOpen={isDrawerOpen}>
              {drawerHeader()}
              <>
                {isDrawerOpen && <SidebarSearch />}
                <FavoritesContainer
                  className={`favorites-container ${
                    !isDrawerOpen && "justify-content-center mt-6 p-6 mb-0 pb-0"
                  } ${
                    isDrawerOpen && "px-1 mx-3 py-2 my-3"
                  } d-flex flex-row justify-between hover:rounded-md transition duration-300`}
                  active={activeItem === "favorite"}
                  isDrawerOpen={isDrawerOpen}
                  onClick={(e) => {
                      e.stopPropagation();
                      setActiveItem("favorite");
                      setSidebarActiveState("favorite");
                      setActive(true);
                      
                      // Use window.location.href to force a full page navigation
                      // instead of React Router navigation if there seems to be a loop
                      if (window.location.pathname !== "/console/favorites") {
                        navigate("/console/favorites");
                      } else {
                        console.log("Already on favorites page, not navigating");
                      }
                    }}
                >
                  <div
                    className="d-flex"
                    onMouseEnter={() => {
                      if (activeItem !== "favorite") {
                        setActive(true);
                      }
                    }}
                    onMouseLeave={() => {
                      if (activeItem !== "favorite") {
                        setActive(false);
                      }
                    }}
                  >
                    <span>
                      {active ? (
                        <FavoriteIcon
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent parent click
                            if (isDrawerOpen === false) {
                              setIsDrawerOpen(true);
                              
                              // Prevent navigation loop
                              if (window.location.pathname !== "/console/favorites") {
                                navigate("/console/favorites");
                              } else {
                                console.log("Already on favorites page, not navigating from icon");
                              }
                            }
                          }}
                        />
                      ) : (
                        <FavoriteBorderIcon style={{ color: "#ffffff" }} />
                      )}
                    </span>
                    {isDrawerOpen && (
                      <span>
                        <p style={{ color: "white" }}>Favorites</p>
                      </span>
                    )}
                  </div>
                  {isDrawerOpen && (
                    <FavoritesNumber
                      className="badge rounded-circle bg-white d-inline-flex justify-content-center align-items-center"
                      style={{
                        backgroundColor: favoritesCount
                          ? "white"
                          : "transparent",
                      }}
                    >
                      {favoritesCount}
                    </FavoritesNumber>
                  )}
                </FavoritesContainer>
                <div className="overflow-y-auto max-h-[calc(100vh-58px-32px-44px-64px-49px)]">
                  {isDrawerOpen && (
                    <Typography
                      className="px-3 pt-3 pb-1"
                      style={{
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        letterSpacing: "0.05rem",
                        color:
                          apptheme.colors.textDisabledColor ||
                          "rgba(255,255,255,0.6)",
                      }}
                    >
                      DOMAINS
                    </Typography>
                  )}
                  <MenuItems isDrawerOpen={isDrawerOpen} />
                  {isDrawerOpen && (
                    <>
                      <div
                        className="mt-2 pt-4 px-2"
                        style={{
                          borderTop: `1px solid ${colors.mediumLightPurple}`,
                        }}
                      >
                        <div
                          className="px-2 py-3 text-sm font-semibold flex items-center justify-between cursor-pointer"
                          style={{
                            color:
                              apptheme.colors.textDisabledColor ||
                              "rgba(255,255,255,0.6)",
                          }}
                        >
                          <div className="flex items-center">
                            <div style={{ marginRight: "8px" }}>
                              <HistoryIcon />
                            </div>
                            <Typography
                              style={{
                                fontSize: "0.8rem",
                                fontWeight: 600,
                                letterSpacing: "0.05rem",
                              }}
                            >
                              RECENT HISTORY
                            </Typography>
                          </div>
                        </div>
                        
                        {Object.entries(conversationsList).length > 0 ? (
                          <div>
                            {/* Today section */}
                            <div className="mb-2">
                              <CategoryHeader 
                                className="d-flex flex-row justify-content-between align-items-center mb-1 cursor-pointer px-2 py-1 category-header"
                                onClick={() => setHistorySections({...historySections, today: !historySections.today})}
                                style={{
                                  borderRadius: "8px",
                                  backgroundColor: "transparent"
                                }}
                              >
                                <p className="user-select-none font-bold m-0" style={{ color: apptheme.name === "light-theme" ? "white" : apptheme.colors.textColor }}>Today</p>
                                <div>
                                  {historySections.today ? <ArrowUp /> : <ArrowDown />}
                                </div>
                              </CategoryHeader>
                              {historySections.today && (
                                <>
                                  {groupConversationsByDate(conversationsList).today.length > 0 ? (
                                    groupConversationsByDate(conversationsList).today.map(({key, conversation}) => (
                                      <SidebarChatRow
                                        key={conversation._id || key}
                                        onClick={() => handleSelectConversation(conversation)}
                                        className="w-full text-left px-3 py-1.5 text-sm rounded-md transition-colors flex justify-between items-center cursor-pointer"
                                      >
                                        <span style={{ 
                                          color: apptheme.name === "light-theme" ? "white" : apptheme.colors.textColor,
                                          maxWidth: "150px",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap"
                                        }}>{conversation.conversationTitle}</span>
                                      </SidebarChatRow>
                                    ))
                                  ) : null}
                                </>
                              )}
                            </div>
                            
                            {/* Yesterday section */}
                            <div className="mb-2">
                              <CategoryHeader 
                                className="d-flex flex-row justify-content-between align-items-center mb-1 cursor-pointer px-2 py-1 category-header"
                                onClick={() => setHistorySections({...historySections, yesterday: !historySections.yesterday})}
                                style={{
                                  borderRadius: "8px",
                                  backgroundColor: "transparent"
                                }}
                              >
                                <p className="user-select-none font-bold m-0" style={{ color: apptheme.name === "light-theme" ? "white" : apptheme.colors.textColor }}>Yesterday</p>
                                <div>
                                  {historySections.yesterday ? <ArrowUp /> : <ArrowDown />}
                                </div>
                              </CategoryHeader>
                              {historySections.yesterday && (
                                <>
                                  {groupConversationsByDate(conversationsList).yesterday.length > 0 ? (
                                    groupConversationsByDate(conversationsList).yesterday.map(({key, conversation}) => (
                                      <SidebarChatRow
                                        key={conversation._id || key}
                                        onClick={() => handleSelectConversation(conversation)}
                                        className="w-full text-left px-3 py-1.5 text-sm rounded-md transition-colors flex justify-between items-center cursor-pointer"
                                      >
                                        <span style={{ 
                                          color: apptheme.name === "light-theme" ? "white" : apptheme.colors.textColor,
                                          maxWidth: "150px",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap"
                                        }}>{conversation.conversationTitle}</span>
                                      </SidebarChatRow>
                                    ))
                                  ) : null}
                                </>
                              )}
                            </div>
                            
                            {/* Past Week section */}
                            <div className="mb-2">
                              <CategoryHeader 
                                className="d-flex flex-row justify-content-between align-items-center mb-1 cursor-pointer px-2 py-1 category-header"
                                onClick={() => setHistorySections({...historySections, pastWeek: !historySections.pastWeek})}
                                style={{
                                  borderRadius: "8px",
                                  backgroundColor: "transparent"
                                }}
                              >
                                <p className="user-select-none font-bold m-0" style={{ color: apptheme.name === "light-theme" ? "white" : apptheme.colors.textColor }}>Past Week</p>
                                <div>
                                  {historySections.pastWeek ? <ArrowUp /> : <ArrowDown />}
                                </div>
                              </CategoryHeader>
                              {historySections.pastWeek && (
                                <>
                                  {groupConversationsByDate(conversationsList).pastWeek.length > 0 ? (
                                    groupConversationsByDate(conversationsList).pastWeek.map(({key, conversation}) => (
                                      <SidebarChatRow
                                        key={conversation._id || key}
                                        onClick={() => handleSelectConversation(conversation)}
                                        className="w-full text-left px-3 py-1.5 text-sm rounded-md transition-colors flex justify-between items-center cursor-pointer"
                                      >
                                        <span style={{ 
                                          color: apptheme.name === "light-theme" ? "white" : apptheme.colors.textColor,
                                          maxWidth: "150px",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap"
                                        }}>{conversation.conversationTitle}</span>
                                      </SidebarChatRow>
                                    ))
                                  ) : null}
                                </>
                              )}
                            </div>
                            
                            {/* Last Month section */}
                            <div className="mb-2">
                              <CategoryHeader 
                                className="d-flex flex-row justify-content-between align-items-center mb-1 cursor-pointer px-2 py-1 category-header"
                                onClick={() => setHistorySections({...historySections, lastMonth: !historySections.lastMonth})}
                                style={{
                                  borderRadius: "8px",
                                  backgroundColor: "transparent"
                                }}
                              >
                                <p className="user-select-none font-bold m-0" style={{ color: apptheme.name === "light-theme" ? "white" : apptheme.colors.textColor }}>Last Month</p>
                                <div>
                                  {historySections.lastMonth ? <ArrowUp /> : <ArrowDown />}
                                </div>
                              </CategoryHeader>
                              {historySections.lastMonth && (
                                <>
                                  {groupConversationsByDate(conversationsList).lastMonth.length > 0 ? (
                                    groupConversationsByDate(conversationsList).lastMonth.map(({key, conversation}) => (
                                      <SidebarChatRow
                                        key={conversation._id || key}
                                        onClick={() => handleSelectConversation(conversation)}
                                        className="w-full text-left px-3 py-1.5 text-sm rounded-md transition-colors flex justify-between items-center cursor-pointer"
                                      >
                                        <span style={{ 
                                          color: apptheme.name === "light-theme" ? "white" : apptheme.colors.textColor,
                                          maxWidth: "150px",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap"
                                        }}>{conversation.conversationTitle}</span>
                                      </SidebarChatRow>
                                    ))
                                  ) : null}
                                </>
                              )}
                            </div>
                            
                            {/* Older section */}
                            <div className="mb-2">
                              <CategoryHeader 
                                className="d-flex flex-row justify-content-between align-items-center mb-1 cursor-pointer px-2 py-1 category-header"
                                onClick={() => setHistorySections({...historySections, older: !historySections.older})}
                                style={{
                                  borderRadius: "8px",
                                  backgroundColor: "transparent"
                                }}
                              >
                                <p className="user-select-none font-bold m-0" style={{ color: apptheme.name === "light-theme" ? "white" : apptheme.colors.textColor }}>Older</p>
                                <div>
                                  {historySections.older ? <ArrowUp /> : <ArrowDown />}
                                </div>
                              </CategoryHeader>
                              {historySections.older && (
                                <>
                                  {groupConversationsByDate(conversationsList).older.length > 0 ? (
                                    groupConversationsByDate(conversationsList).older.map(({key, conversation}) => (
                                      <SidebarChatRow
                                        key={conversation._id || key}
                                        onClick={() => handleSelectConversation(conversation)}
                                        className="w-full text-left px-3 py-1.5 text-sm rounded-md transition-colors flex justify-between items-center cursor-pointer"
                                      >
                                        <span style={{ 
                                          color: apptheme.name === "light-theme" ? "white" : apptheme.colors.textColor,
                                          maxWidth: "150px",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap"
                                        }}>{conversation.conversationTitle}</span>
                                      </SidebarChatRow>
                                    ))
                                  ) : null}
                                </>
                              )}
                            </div>
                          </div>
                        ) : null}
                      </div>
                      <div
                        className="p-3 d-flex flex-row align-items-center"
                        style={{
                          borderTop: `1px solid ${
                            apptheme.colors.lightGreyRgba ||
                            "rgba(85, 85, 85, .4)"
                          }20`,
                          marginTop: "8px",
                          paddingTop: "16px",
                        }}
                      >
                        <SpaceInvaderCanvas
                          text={amsUser?.email.split("@")[0] || "default"}
                          size="md"
                        />
                        <Typography
                          style={{
                            marginLeft: "12px",
                            fontWeight: 500,
                            fontSize: "0.95rem",
                            color: "white",
                          }}
                        >
                          {amsUser?.username}
                        </Typography>
                      </div>
                    </>
                  )}
                </div>
              </>
            </SidebarItemsContainer>
          </CustomDrawer>
        </>
      )}

      {/* For the Mobile/Tablet */}
      {isSmallerThan && (
        <Drawer
          open={isDrawerOpen}
          onClose={() => handleHeaderIconClick({ openDrawer: false })}
          variant="temporary"
          sx={{
            "& .MuiDrawer-paper": {
              minWidth: "300px",
              transition: "transform 0.3s ease-out !important",
              background: `${apptheme.colors.dark} !important`,
            },
          }}
        >
          <StyledHeader className="mobile-header">
            <div className="header-button-and-logo">
              <span
                className="hamburger-icon"
                onClick={() => handleHeaderIconClick({ openDrawer: false })}
              >
                <CrossBtnIcon />
              </span>

              <IconButton disableRipple disabled className="rounded-0">
                <NavLogo
                  src={
                    apptheme.name === "dark-theme"
                      ? AccentureWhiteLogo
                      : AccentureBlackLogo
                  }
                />
              </IconButton>
            </div>
          </StyledHeader>
          <span className="mobile w-max-content">
            <MenuItems isDrawerOpen={true} />
          </span>
        </Drawer>
      )}
    </SidebarDataLayout>
  );
}